import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import useTranslation from 'next-translate/useTranslation';
import { useLinkResolver } from 'prismic/helpers/useLinkResolver';
import { PrismicExternalLink, PrismicLink } from 'prismic/types';
import { fetchCourses } from 'prismic/api/courses';
import getHeadingTag from 'helpers/getHeadingTag';
import getHeadingSkin from 'helpers/getHeadingSkin';
import { BreakpointContext } from 'contexts/breakpoint/BreakpointContext';
import { DomainContext } from 'contexts/domain/DomainContext';
import { KursSliderItemProps } from 'components/molecules/KursSliderItem/KursSliderItem.types';
import { mapKursSliderItem } from 'components/molecules/KursSliderItem/KursSliderItem.helper';
import KursSliderItem from 'components/molecules/KursSliderItem/KursSliderItem';
import Slider from 'components/molecules/Slider/Slider';
import Column from 'components/layout/Grid/Column';
import { Loading } from 'components/atoms/Loading';
import Link from 'components/atoms/Link/Link';
import {
  Wrapper,
  HeadlineCol,
  HeadingStyled,
  ColumnLinkStyled,
  StyledMobileLink,
} from './KurseSammlung.styles';
import { KurseSammlungProps } from './KurseSammlung.types';

export const KurseSammlung: React.FC<KurseSammlungProps> = ({ className, primary, items }) => {
  const { t } = useTranslation('common');
  const { locale } = useLinkResolver();
  const bp = useContext(BreakpointContext);
  const isMobile = bp === 'xs' || bp === 'sm';
  const {
    state: { domain },
  } = useContext(DomainContext);

  const { title, link = null, link_text = '' } = primary;
  const hasHeadline = title?.[0]?.text;

  const fallbackLink: PrismicExternalLink = {
    link_type: 'Web',
    url: 'https://shop.ballenberg.ch/de/kursuebersicht',
  };

  const isExternalLink =
    !!(link as PrismicLink)?.url ||
    (link as PrismicLink)?.link_type === 'Web' ||
    (link as PrismicLink)?.link_type === 'Media';

  const resolvedLink: PrismicLink | PrismicExternalLink =
    link && !(link as PrismicLink)?.isBroken && isExternalLink ? link : fallbackLink;

  let sliderItems: KursSliderItemProps[] = [];

  /* ------------------------------------------------------------------
   * 1. Split CMS items: manual (shop_link) vs legacy (anlassnr)
   * ------------------------------------------------------------------ */
  const manualItems = items.filter((item) => item.shop_link?.url);
  const legacyItems = items.filter((item) => item.anlassnr && !item.shop_link?.url);

  const limitedManualItems = manualItems.slice(0, 3);
  const limitedLegacyItems = legacyItems.slice(0, 3);

  /* ------------------------------------------------------------------
   * 2. Add MANUAL items directly from CMS (2026 ready)
   * ------------------------------------------------------------------ */
limitedManualItems.forEach((item) => {
  const mappedItem: KursSliderItemProps = {
    image: item.kurs_bild,
    text: item.kurs_titel ?? '',
    slot1: item.datum ?? '',
    slot2: '',
    link: item.shop_link ?? null,
  };

  sliderItems.push(mappedItem);
});



  /* ------------------------------------------------------------------
   * 3. Fetch LEGACY items from API only if needed
   * ------------------------------------------------------------------ */
  const fetchQuery = async () => {
    if (limitedLegacyItems.length === 0) {
      return null;
    }

    let queryArray: string[] = [];

    limitedLegacyItems.forEach((cmsItem) => {
      if (cmsItem?.anlassnr) {
        queryArray.push(`anlassnr=${cmsItem.anlassnr}`);
      }
    });

    let queryString = queryArray.length > 0 ? '?' + queryArray.join('&') : '';

    if (
      process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview' ||
      process.env.NEXT_PUBLIC_VERCEL_ENV === 'development'
    ) {
      console.warn('[KurseSammlung] STAGING mode – loading all courses');
      queryString = '';
    }

    return await fetchCourses({ domain, query: queryString });
  };

  const { data, error, isLoading } = useQuery(['kurseHome'], fetchQuery, {
    enabled: limitedLegacyItems.length > 0,
  });

  const getError = (error: unknown): string => {
    console.error('[KurseSammlung]', error);
    return t('apiErrors.errorDefaultMessage');
  };

  console.log('[KurseSammlung] API response:', data);

  /* ------------------------------------------------------------------
   * 4. Map LEGACY API courses
   * ------------------------------------------------------------------ */
  if (data && Array.isArray(data?.data || data)) {
    const kursList = data?.data || data;

    kursList.forEach((kursItem) => {
      const item = mapKursSliderItem(locale, kursItem);
      sliderItems.push(item);
    });
  }
// if (sliderItems.length === 0) {
//   return null;
// }
const noCourses =
  limitedManualItems.length === 0 && limitedLegacyItems.length === 0;

const shouldShowLoader =
  isLoading && limitedLegacyItems.length > 0 && !noCourses;


  /* ------------------------------------------------------------------
   * 5. Render
   * ------------------------------------------------------------------ */
  return (
    <div className={className}>
      <Wrapper>
        {hasHeadline && (
          <HeadlineCol span={12}>
            {title?.[0]?.text && (
              <HeadingStyled
                skin={getHeadingSkin(title[0].type)}
                elementType={getHeadingTag(title[0].type)}
              >
                {title[0]?.text}
              </HeadingStyled>
            )}
            {resolvedLink && !isMobile && (
              <Link skin="tertiaryBig" url={resolvedLink} aria-label="Zum Shop" />
            )}

          </HeadlineCol>
        )}

        <Column span={12}>
          {error && <div>{getError(error)}</div>}
{shouldShowLoader && <Loading />}

          <Slider
            slidesPerView={{
              xs: 1,
              sm: 1.5,
              md: 2,
              lg: 3,
              xl: 3,
              xxl: 3,
              xxxl: 3,
            }}
            spaceBetween={20}
            centeredSlides={false}
          >
            {sliderItems.map((item, index) => (
              <KursSliderItem key={`kurs-item-${index}`} data={item} />
            ))}
          </Slider>
        </Column>

        <ColumnLinkStyled span={12}>
          {resolvedLink && isMobile && (
            <StyledMobileLink skin="tertiaryBig" url={resolvedLink}>
              {link_text || resolvedLink.url}
            </StyledMobileLink>
          )}
        </ColumnLinkStyled>
      </Wrapper>
    </div>
  );
};
